import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { SearchConfig } from './AppLayout';
import { Bell, Menu, Search } from 'lucide-react';
import { AdminAlert, notificationsApi } from '../../api/notifications';
import { TWO_FACTOR_PATH } from './MfaRequiredNotice';

interface TopBarProps {
  pageTitle: string;
  searchConfig: SearchConfig | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onMenuClick: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  pageTitle,
  searchConfig,
  searchValue,
  onSearchChange,
  onMenuClick,
}) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  async function loadNotifications() {
    try {
      const [inbox, unread] = await Promise.all([
        notificationsApi.listInbox({ page: 1, limit: 8 }),
        notificationsApi.getUnreadCount(),
      ]);
      setAlerts(inbox.items);
      setUnreadCount(unread.count);
    } catch {
      // Keep the header quiet if notifications are unavailable.
    }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(e.target as Node)
      ) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    void loadNotifications();
    const timer = window.setInterval(() => {
      void loadNotifications();
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAlertClick = async (alert: AdminAlert) => {
    try {
      if (!alert.readAt) {
        await notificationsApi.markInboxRead(alert.id);
      }
    } catch {
      // Navigation is still useful even if read state update fails.
    }
    setNotificationsOpen(false);
    void loadNotifications();
    if (alert.targetUrl.startsWith('http')) {
      window.location.href = alert.targetUrl;
      return;
    }
    navigate(alert.targetUrl);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markInboxAllRead();
      await loadNotifications();
    } catch {
      // Silent; this dropdown should not interrupt admin work.
    }
  };

  if (!admin) return null;

  const initials = `${admin.firstName[0]}${admin.lastName[0]}`.toUpperCase();
  const roleLabel = admin.role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  return (
    <header
      className="admin-topbar"
      style={{
        height: 64,
        background: 'var(--neutral-100)',
        borderBottom: '1px solid var(--neutral-300)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        gap: 24,
      }}
    >
      <div className="admin-topbar-left" style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1 }}>
        <button className="admin-topbar-menu-button" type="button" aria-label="Open navigation" onClick={onMenuClick}>
          <Menu size={20} />
        </button>
        <h1
          className="admin-topbar-title"
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--neutral-800)',
            margin: 0,
            minWidth: 'fit-content',
          }}
        >
          {pageTitle}
        </h1>

        {searchConfig && (
          <div
            className="admin-topbar-search"
            style={{
              flex: 1,
              maxWidth: 480,
              position: 'relative',
            }}
          >
            <Search
                size={15}
                style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--neutral-500)',
                pointerEvents: 'none',
                }}
            />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchConfig.placeholder}
              style={{
                width: '100%',
                height: 38,
                padding: '0 12px 0 36px',
                fontSize: 13,
                fontFamily: 'var(--font-family)',
                color: 'var(--neutral-800)',
                background: 'var(--neutral-200)',
                border: '1px solid transparent',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                transition: 'all 150ms ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = 'var(--neutral-100)';
                e.currentTarget.style.borderColor = 'var(--blue-600)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = 'var(--neutral-200)';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div ref={notificationsRef} style={{ position: 'relative' }}>
        <button
          onClick={() => {
            setNotificationsOpen((open) => !open);
            if (!notificationsOpen) void loadNotifications();
          }}
          aria-label="Open notifications"
          style={{
            width: 38,
            height: 38,
            borderRadius: 'var(--radius-full)',
            background: notificationsOpen ? 'var(--neutral-200)' : 'transparent',
            color: 'var(--neutral-700)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Bell size={19} />
          {unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: 3,
                right: 2,
                minWidth: 17,
                height: 17,
                borderRadius: 9,
                background: 'var(--danger-600)',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {notificationsOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              background: 'var(--neutral-100)',
              border: '1px solid var(--neutral-300)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              overflow: 'hidden',
              zIndex: 20,
            }}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--neutral-300)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--neutral-800)' }}>
                  Notifications
                </div>
                <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginTop: 2 }}>
                  Priority alerts for your role
                </div>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  style={{
                    fontSize: 12,
                    color: 'var(--blue-700)',
                    fontWeight: 600,
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {alerts.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--neutral-500)',
                    fontSize: 13,
                  }}
                >
                  No notifications yet
                </div>
              ) : (
                alerts.map((alert) => (
                  <button
                    key={alert.id}
                    onClick={() => void handleAlertClick(alert)}
                    style={{
                      width: '100%',
                      border: 0,
                      borderBottom: '1px solid var(--neutral-200)',
                      background: alert.readAt ? 'transparent' : 'var(--blue-100)',
                      padding: 14,
                      display: 'flex',
                      gap: 12,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        marginTop: 6,
                        background: priorityColor(alert.priority),
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <strong style={{ fontSize: 13, color: 'var(--neutral-800)' }}>
                          {alert.title}
                        </strong>
                        <span
                          style={{
                            fontSize: 10,
                            color: priorityColor(alert.priority),
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}
                        >
                          {alert.priority}
                        </span>
                      </span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12,
                          color: 'var(--neutral-600)',
                          lineHeight: 1.45,
                        }}
                      >
                        {alert.body}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 11,
                          color: 'var(--neutral-500)',
                          marginTop: 6,
                        }}
                      >
                        {formatAlertDate(alert.createdAt)}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 12px 6px 6px',
            borderRadius: 'var(--radius-full)',
            background: menuOpen ? 'var(--neutral-200)' : 'transparent',
            transition: 'background 150ms ease',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--blue-600)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              overflow: 'hidden',
            }}
          >
            {admin.profilePhoto ? (
              <img
                src={admin.profilePhoto}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </div>
          <div className="admin-topbar-user-label" style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}>
              {admin.firstName} {admin.lastName}
            </div>
            <div style={{ fontSize: 11, color: 'var(--neutral-500)' }}>{roleLabel}</div>
          </div>
        </button>

        {menuOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              background: 'var(--neutral-100)',
              border: '1px solid var(--neutral-300)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              minWidth: 200,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--neutral-300)' }}>
              <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>Signed in as</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)', marginTop: 2, wordBreak: 'break-all' }}>
                {admin.email}
              </div>
            </div>
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate('/settings');
              }}
              style={{
                width: '100%',
                padding: '10px 16px',
                textAlign: 'left',
                fontSize: 13,
                color: 'var(--neutral-700)',
                fontWeight: 500,
                transition: 'background 150ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--neutral-200)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Settings
            </button>
            {/* Otherwise only reachable from the MFA notice — which an admin
                sees after being refused, not before. */}
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate(TWO_FACTOR_PATH);
              }}
              style={{
                width: '100%',
                padding: '10px 16px',
                textAlign: 'left',
                fontSize: 13,
                color: 'var(--neutral-700)',
                fontWeight: 500,
                transition: 'background 150ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--neutral-200)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Two-factor authentication
            </button>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px 16px',
                textAlign: 'left',
                fontSize: 13,
                color: 'var(--danger-600)',
                fontWeight: 500,
                transition: 'background 150ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--danger-100)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
      </div>
    </header>
  );
};

function priorityColor(priority: AdminAlert['priority']) {
  if (priority === 'urgent') return 'var(--danger-600)';
  if (priority === 'high') return 'var(--warning-900)';
  if (priority === 'normal') return 'var(--blue-700)';
  return 'var(--neutral-500)';
}

function formatAlertDate(value: string) {
  return new Date(value).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
